import { TuiRoot } from '@taiga-ui/core'
import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	inject,
	OnDestroy,
	OnInit,
	Renderer2,
	signal,
	WritableSignal,
} from '@angular/core'
import { Router, RouterOutlet } from '@angular/router'
import { LanguageService } from '@modules/lang-module/services/language.service'
import { catchError, concatMap, interval, merge, Observable, of, Subject, takeUntil, timer } from 'rxjs'
import { ThemeEnum } from '@modules/theme-module/enums/theme.enum'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { WebsocketService } from '@modules/websocket-module/services/websocket.service'
import { ScreenService } from '@shared/services/screen.service'
import { MsInMinute } from '@shared/constants/times/ms-in-minute'
import { ThemeService } from '@modules/theme-module/services/theme.service'
import { EnvironmentsConfigsInterface } from '@environments/core/interfaces/environments-configs.interface'
import { EnvironmentsService } from '@environments/core/services/environments.service'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-root',
	imports: [RouterOutlet, TuiRoot],
	templateUrl: './app.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
	protected readonly themeEnum: typeof ThemeEnum = ThemeEnum
	protected readonly msInMinute: number = MsInMinute
	protected readonly timeoutMsAFK: number = 10 * this.msInMinute

	private _destroy$: Subject<void> = new Subject<void>()
	private _destroyAFKTimer$: Subject<void> = new Subject<void>()

	private readonly _renderer: Renderer2 = inject<Renderer2>(Renderer2)
	private readonly _languageService: LanguageService = inject<LanguageService>(LanguageService)
	private readonly _screenService: ScreenService = inject<ScreenService>(ScreenService)
	private readonly _websocketService: WebsocketService<unknown, unknown> =
		inject<WebsocketService<unknown, unknown>>(WebsocketService)
	private readonly _themeService: ThemeService = inject<ThemeService>(ThemeService)
	private readonly _environmentsService: EnvironmentsService = inject<EnvironmentsService>(EnvironmentsService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _router: Router = inject<Router>(Router)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	private _isWasAFK: boolean = false

	protected theme: WritableSignal<ThemeEnum> = signal(this.themeEnum.LIGHT)

	get isAFK(): boolean {
		return this._screenService.isAFK
	}

	private _generateDeviceId(): void {
		this._userHelperService.generateDeviceId()
	}

	private _listenTheme(): void {
		this._themeService.initTheme()

		this._themeService.theme$.pipe(takeUntil(this._destroy$)).subscribe((theme: ThemeEnum): void => {
			this.theme.set(theme)
			this._applyBodyThemeClass()
		})
	}

	private _applyBodyThemeClass(): void {
		const bodyEl: HTMLElement = document.body

		this._renderer.removeClass(bodyEl, this.themeEnum.LIGHT)
		this._renderer.removeClass(bodyEl, this.themeEnum.DARK)

		this._renderer.addClass(bodyEl, this.theme())
	}

	private _listenLanguage(): void {
		this._languageService.initLanguage()

		this._languageService.language$.pipe(takeUntil(this._destroy$)).subscribe((lang: LanguageEnum): void => {
			this._renderer.setAttribute(document.documentElement, 'lang', lang)
		})
	}

	private _listenWasAFK(): void {
		this._screenService.isWasAFK$.pipe(takeUntil(this._destroy$)).subscribe((isWasAFK: boolean): void => {
			this._isWasAFK = isWasAFK
		})
	}

	private _startTimerAFK(): void {
		timer(this.timeoutMsAFK)
			.pipe(takeUntil(merge(this._destroy$, this._destroyAFKTimer$)))
			.subscribe((): void => {
				this._screenService.changeAFKState(true)
				this._websocketService.disconnectAll()
			})
	}

	private _processAFK(): void {
		if (this.isAFK) {
			this._startTimerAFK()
			return
		}
		if (this._isWasAFK) location.reload()
		this._destroyAFKTimer$.next()
	}

	private _startTimerEnvironmentsConfigs(): void {
		interval(this.msInMinute)
			.pipe(
				takeUntil(this._destroy$),
				concatMap(
					(): Observable<EnvironmentsConfigsInterface | null> =>
						this._environmentsService
							.getEnvironmentsConfigs()
							.pipe(catchError((): Observable<null> => of(null))),
				),
			)
			.subscribe()
	}

	private _listenEnvironmentsConfigs(): void {
		this._environmentsHelperService.environmentsConfigs$
			.pipe(takeUntil(this._destroy$))
			.subscribe((config: EnvironmentsConfigsInterface | null): void => {
				let isTechWorks: boolean = !!config?.tech_works
				const isTechWorksUrl: boolean = this._router.url.includes('technical-works')

				if (isTechWorks) {
					this._websocketService.disconnectAll()
					this._tradingLauncherService.dropStates()
					this._router.navigate(['technical-works'])
				} else if (isTechWorksUrl) {
					this._router.navigate(['cabinet'])
				}
			})
	}

	@HostListener('document:visibilitychange')
	public onVisibilityChange(): void {
		this._processAFK()
	}

	ngOnInit(): void {
		this._generateDeviceId()
		this._startTimerEnvironmentsConfigs()
		this._listenEnvironmentsConfigs()
		this._listenTheme()
		this._listenLanguage()
		this._listenWasAFK()
		this._processAFK()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
